import { FormControl, TextField } from "@mui/material";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { LeadAgencyPicker } from "components/LeadAgencyPicker";
import { PoliceDepartmentPicker } from "components/PoliceDepartmentPicker";
import { Control, Controller } from "react-hook-form";

import { RecipientType } from "../../utils";
import { useRecipientForm } from "../hooks/useRecipientForm";
import { GroupedFireDepartmentPickerWithFacilityFilter } from "./GroupedFireDepartmentPickerWithFacilityFilter";
import { GroupedLepcPickerWithFacilityFilter } from "./GroupedLepcPickerWithFacilityFilter";

interface RecipientPickerProps {
  recipientType: RecipientType | undefined;
  facilityIds: string[];
  control: Control<any>;
  disabled?: boolean;
  index: number;
}

export const RecipientPicker = ({
  recipientType,
  facilityIds,
  control,
  disabled = false,
  index,
}: RecipientPickerProps) => {
  const {
    onChooseLepc,
    onChooseFireDepartment,
    onChoosePoliceDepartment,
    onChooseLeadAgency,
    lepc,
    fireDepartment,
    issues,
  } = useRecipientForm(index);

  const recipientNameIssue = issues.find(
    (issue) => issue.key === `recipients.${index}.recipientName`
  );

  const recipientNameError = recipientNameIssue
    ? {
        type: "validation",
        message: recipientNameIssue?.message,
      }
    : undefined;

  switch (recipientType) {
    case RecipientType.LEPC:
      return (
        <Controller
          name={`recipients.${index}.lepc`}
          control={control}
          rules={{ required: "LEPC selection is required" }}
          render={({ field, fieldState }) => (
            <FormControl fullWidth error={!!fieldState.error}>
              <GroupedLepcPickerWithFacilityFilter
                facilityIds={facilityIds}
                {...fieldState}
                error={fieldState.error ?? recipientNameError}
                value={field.value}
                onChange={(value) => {
                  field.onChange(value);
                  if (value?.id) {
                    onChooseLepc(value);
                  }
                }}
                disabled={disabled}
                helperText={
                  lepc ? (
                    <ul>
                      {[
                        {
                          label: "Is submitted via portal",
                          enabled: lepc.isSubmittedViaPortal,
                        },
                        {
                          label: "Is submitted by the state",
                          enabled: lepc.isStateSubmits,
                        },
                        { label: "Needs T2s File", enabled: lepc.needsT2sFile },
                        {
                          label: "Needs XML Zip File",
                          enabled: lepc.needsXmlZipFile,
                        },
                        { label: "Is Deactivated", enabled: lepc.deactivated },
                      ]
                        .filter((field) => field.enabled)
                        .map((field, index) => (
                          <li key={index}>{field.label}</li>
                        ))}
                    </ul>
                  ) : undefined
                }
                displayAgencyLink
              />
            </FormControl>
          )}
        />
      );
    case RecipientType.FIRE_DEPARTMENT:
      return (
        <Controller
          name={`recipients.${index}.fireDepartment`}
          control={control}
          rules={{ required: "Fire Department selection is required" }}
          render={({ field, fieldState }) => (
            <FormControl fullWidth error={!!fieldState.error}>
              <GroupedFireDepartmentPickerWithFacilityFilter
                facilityIds={facilityIds}
                value={field.value}
                {...fieldState}
                error={fieldState.error ?? recipientNameError}
                onChange={(value) => {
                  field.onChange(value);
                  if (value?.id) {
                    onChooseFireDepartment(value);
                  }
                }}
                disabled={disabled}
                helperText={
                  fireDepartment ? (
                    <ul>
                      {[
                        {
                          label: "Is submitted via portal",
                          enabled: fireDepartment.isSubmittedViaPortal,
                        },
                        {
                          label: "Is submitted by the state",
                          enabled: fireDepartment.stateSubmitsToFireDept,
                        },
                        {
                          label: "Needs T2s File",
                          enabled: fireDepartment.needsT2sFile,
                        },
                        {
                          label: "Is Deactivated",
                          enabled: fireDepartment.deactivated,
                        },
                      ]
                        .filter((field) => field.enabled)
                        .map((field, index) => (
                          <li key={index}>{field.label}</li>
                        ))}
                    </ul>
                  ) : undefined
                }
                displayAgencyLink
              />
            </FormControl>
          )}
        />
      );
    case RecipientType.POLICE_DEPARTMENT:
      return (
        <Controller
          name={`recipients.${index}.policeDepartment`}
          control={control}
          rules={{ required: "Police Department selection is required" }}
          render={({ field, fieldState }) => (
            <FormControl fullWidth error={!!fieldState.error}>
              <PoliceDepartmentPicker
                value={field.value}
                {...fieldState}
                error={fieldState.error ?? recipientNameError}
                onChange={(value) => {
                  field.onChange(value);
                  if (value?.id) {
                    onChoosePoliceDepartment(value);
                  }
                }}
                disabled={disabled}
                displayAgencyLink
              />
            </FormControl>
          )}
        />
      );
    case RecipientType.LEAD_AGENCY:
      return (
        <Controller
          name={`recipients.${index}.leadAgency`}
          control={control}
          rules={{ required: "Lead Agency selection is required" }}
          render={({ field, fieldState }) => (
            <FormControl fullWidth error={!!fieldState.error}>
              <LeadAgencyPicker
                value={field.value}
                {...fieldState}
                error={fieldState.error ?? recipientNameError}
                onChange={(value) => {
                  field.onChange(value);
                  if (value?.id) {
                    onChooseLeadAgency(value);
                  }
                }}
                disabled={disabled}
                displayAgencyLink
              />
            </FormControl>
          )}
        />
      );
    case RecipientType.CUSTOM:
    default:
      return (
        <Controller
          name={`recipients.${index}.recipientName`}
          control={control}
          render={({ field, fieldState }) => (
            <>
              <TextField
                {...field}
                value={field.value ?? ""}
                label="Recipient"
                disabled={disabled}
                error={!!fieldState.error}
                fullWidth
                onChange={(event) => {
                  field.onChange(event);
                }}
              />
              <ErrorDisplay
                error={fieldState.error}
                helperText={fieldState.error?.message}
                hideErrors={false}
              />
            </>
          )}
        />
      );
  }
};
